import { parsePositiveId } from "../../tasksList/lib/taskListPagination";
import {
  VALID_STATUSES,
  VALID_PRIORITIES,
} from "../../tasksList/lib/taskListQuery";

// Month codec: YYYY-MM
export const MONTH_PARAM_RE = /^\d{4}-(?:0[1-9]|1[0-2])$/;

const normalizeEnumList = (values, allowed) => {
  const seen = new Set();
  const result = [];
  for (const raw of values) {
    const value = String(raw);
    if (!allowed.includes(value) || seen.has(value)) continue;
    seen.add(value);
    result.push(value);
  }
  return result;
};

export function currentLocalMonth() {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  return `${yyyy}-${mm}`;
}

export function parseMonthParam(raw) {
  if (raw && MONTH_PARAM_RE.test(raw)) return raw;
  return currentLocalMonth();
}

export function isValidMonthParam(raw) {
  return raw != null && MONTH_PARAM_RE.test(raw);
}

// Day codec: YYYY-MM-DD
export const DAY_PARAM_RE = /^\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])$/;

export function parseDayParam(raw, month) {
  if (!raw || !DAY_PARAM_RE.test(raw)) return null;
  // Must belong to the displayed month
  if (!raw.startsWith(month + "-")) return null;
  // Validate that the day is a real calendar date
  const d = new Date(raw + "T00:00:00");
  if (Number.isNaN(d.getTime())) return null;
  const localIso = localDateString(d);
  if (localIso !== raw) return null;
  return raw;
}

export function localDateString(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function parseShowDoneParam(raw) {
  return raw === "1";
}

const sameEnumList = (a, b) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

/**
 * URL-backed invariant between show_done and status filters:
 * - selecting status=done implies show_done=true
 * - show_done=false strips done from status filters
 */
export function reconcileStatusShowDone(showDone, statuses = []) {
  const selected = normalizeEnumList(statuses, VALID_STATUSES);
  if (selected.includes("done")) {
    return { showDone: true, statuses: selected };
  }
  return {
    showDone: Boolean(showDone),
    statuses: selected.filter((status) => status !== "done"),
  };
}

export function todayLocalDate() {
  return localDateString(new Date());
}

/**
 * When the displayed month is the current local month and `day` is absent
 * (or was invalid and stripped), select today's local date.
 * Non-current months never invent a selected day.
 */
export function resolveAutoSelectedDay(month, day) {
  if (day) return day;
  if (month !== currentLocalMonth()) return null;
  return todayLocalDate();
}

export function parseCalendarSearchParams(searchParams) {
  const rawMonth = searchParams.get("month");
  const month = parseMonthParam(rawMonth);
  const needsMonthNormalize = !isValidMonthParam(rawMonth);

  const rawDay = searchParams.get("day");
  let day = parseDayParam(rawDay, month);
  const needsInvalidDayNormalize = rawDay != null && day == null;
  const autoDay = resolveAutoSelectedDay(month, day);
  const needsTodaySelectNormalize = day == null && autoDay != null;
  if (needsTodaySelectNormalize) {
    day = autoDay;
  }
  const needsDayNormalize = needsInvalidDayNormalize || needsTodaySelectNormalize;

  const rawShowDone = searchParams.get("show_done");
  const rawShowDoneParsed = parseShowDoneParam(rawShowDone);
  const needsRawShowDoneTokenNormalize =
    rawShowDone != null && rawShowDone !== "1" && rawShowDone !== undefined;

  const rawClient = searchParams.get("client");
  const clientId = parsePositiveId(rawClient);
  const needsClientNormalize =
    rawClient != null && rawClient !== "" && clientId == null;

  const rawProperty = searchParams.get("property");
  const propertyId = parsePositiveId(rawProperty);
  const needsPropertyNormalize =
    rawProperty != null && rawProperty !== "" && propertyId == null;

  const rawStatuses = searchParams.getAll("status");
  const statusesRaw = normalizeEnumList(rawStatuses, VALID_STATUSES);
  const needsInvalidStatusNormalize = rawStatuses.some(
    (raw) => !VALID_STATUSES.includes(String(raw)),
  );

  const reconciled = reconcileStatusShowDone(rawShowDoneParsed, statusesRaw);
  const showDone = reconciled.showDone;
  const statuses = reconciled.statuses;
  const needsStatusShowDoneNormalize =
    showDone !== rawShowDoneParsed || !sameEnumList(statuses, statusesRaw);

  const rawPriorities = searchParams.getAll("priority");
  const priorities = normalizeEnumList(rawPriorities, VALID_PRIORITIES);
  const needsPriorityNormalize = rawPriorities.some(
    (raw) => !VALID_PRIORITIES.includes(String(raw)),
  );

  return {
    month,
    day,
    showDone,
    clientId,
    propertyId,
    statuses,
    priorities,
    needsMonthNormalize,
    needsDayNormalize,
    needsShowDoneNormalize:
      needsRawShowDoneTokenNormalize || needsStatusShowDoneNormalize,
    needsClientNormalize,
    needsPropertyNormalize,
    needsStatusNormalize:
      needsInvalidStatusNormalize || needsStatusShowDoneNormalize,
    needsPriorityNormalize,
  };
}

export function buildCalendarSearchParams({
  month,
  day,
  showDone,
  clientId,
  propertyId,
  statuses = [],
  priorities = [],
}) {
  const next = new URLSearchParams();
  if (month) next.set("month", month);
  if (day) next.set("day", day);
  if (showDone) next.set("show_done", "1");
  if (clientId != null) next.set("client", String(clientId));
  if (propertyId != null) next.set("property", String(propertyId));
  for (const status of normalizeEnumList(statuses, VALID_STATUSES)) {
    next.append("status", status);
  }
  for (const priority of normalizeEnumList(priorities, VALID_PRIORITIES)) {
    next.append("priority", priority);
  }
  return next;
}

/**
 * Resolve API status list from show_done + compact status filters.
 * Callers must pass already-reconciled (showDone, statuses).
 * Empty selection defaults; explicit selections pass through unchanged.
 */
export function resolveCalendarStatusFilter(showDone, statuses = []) {
  const reconciled = reconcileStatusShowDone(showDone, statuses);
  if (reconciled.statuses.length === 0) {
    return reconciled.showDone
      ? ["todo", "in_progress", "done"]
      : ["todo", "in_progress"];
  }
  return reconciled.statuses;
}

// Build calendar URL params with only context fields for tab switching
export function buildTabContextParams({ clientId, propertyId } = {}) {
  const next = new URLSearchParams();
  if (clientId != null) next.set("client", String(clientId));
  if (propertyId != null) next.set("property", String(propertyId));
  return next;
}
